import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Info } from 'lucide-react';
import GlowBackground from '../components/GlowBackground';
import Header from '../components/Header';
import PillSelector from '../components/PillSelector';
import InfoModal from '../components/InfoModal';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WavelengthNew = () => {
  const navigate = useNavigate();
  const [level, setLevel] = useState('spark');
  const [type, setType] = useState('general');
  const [isCreating, setIsCreating] = useState(false);
  const [showTypeInfo, setShowTypeInfo] = useState(false);
  const [types, setTypes] = useState([]);
  const [levels, setLevels] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/config/types`),
      axios.get(`${API}/config/levels`)
    ]).then(([typesRes, levelsRes]) => {
      setTypes(typesRes.data.map(t => ({ value: t.key, label: t.label })));
      setLevels(levelsRes.data.map(l => ({ value: l.key, label: l.label, description: l.description })));
    }).catch(() => {});
  }, []);

  const handleCreateSession = async () => {
    setIsCreating(true);
    try {
      const response = await axios.post(`${API}/wavelength/create`, {
        level,
        type
      });
      
      const { slug } = response.data;
      const link = `${window.location.origin}/wavelength/${slug}`;

      try {
        await navigator.clipboard.writeText(link);
        toast.success('Session created — link copied!', {
          description: 'Paste it anywhere to invite someone.'
        });
      } catch {
        toast.success('Session created!', {
          description: 'Share the link to invite someone.'
        });
      }
      
      setTimeout(() => {
        navigate(`/wavelength/${slug}`);
      }, 500);
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Could not create session');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{ backgroundColor: '#FFF8F0' }}
      data-testid="wavelength-new-page"
    >
      <GlowBackground />
      <div className="content-layer">
      <Header showWavelengthButton={false} />
      
      <main className="min-h-screen flex flex-col items-center justify-center pt-20 pb-32 px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-xl w-full"
        >
          <h1 
            className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6"
            data-testid="wavelength-title"
          >
            Start a Wavelength
          </h1>
          
          <p className="text-charcoal/60 text-base md:text-lg mb-10 max-w-md mx-auto leading-relaxed">
            Share a unique link. Same question. Same moment. Both say yes before the conversation begins.
          </p>
          
          {/* Create button */}
          <motion.button
            whileHover={{ scale: 1.02, boxShadow: '0 8px 30px rgba(255, 120, 10, 0.25)' }}
            whileTap={{ scale: 0.98 }}
            onClick={handleCreateSession}
            disabled={isCreating}
            className="px-10 py-5 rounded-full font-medium text-white text-lg transition-[box-shadow,transform,background-color,opacity] duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: '#ff780a',
              boxShadow: '0 4px 20px rgba(255, 120, 10, 0.3)',
            }}
            data-testid="create-wavelength-button"
          >
            {isCreating ? 'Creating...' : 'Start Wavelength'}
          </motion.button>
          
          {/* Pill selectors with annotation callouts */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 flex items-end justify-center gap-3"
          >
            {/* Type selector with annotation */}
            <div className="relative">
              <div 
                className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none"
                style={{ bottom: '100%', marginBottom: '6px' }}
              >
                <span
                  style={{
                    fontFamily: "'Caveat', cursive",
                    fontSize: '1.1rem',
                    color: 'rgba(44, 36, 23, 0.45)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Who is it for?
                </span>
                <svg width="20" height="18" viewBox="0 0 20 18" fill="none">
                  <path d="M10 2 L10 12" stroke="rgba(44, 36, 23, 0.3)" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M6 9 L10 14 L14 9" stroke="rgba(44, 36, 23, 0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </div>
              <PillSelector
                value={type}
                options={types}
                onChange={setType}
                isActive={false}
                testId="wavelength-type-selector"
              />
            </div>

            {/* Level selector with annotation */}
            <div className="relative">
              <div 
                className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none"
                style={{ bottom: '100%', marginBottom: '6px' }}
              >
                <span
                  style={{
                    fontFamily: "'Caveat', cursive",
                    fontSize: '1.1rem',
                    color: 'rgba(44, 36, 23, 0.45)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  How deep?
                </span>
                <svg width="20" height="18" viewBox="0 0 20 18" fill="none">
                  <path d="M10 2 L10 12" stroke="rgba(44, 36, 23, 0.3)" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M6 9 L10 14 L14 9" stroke="rgba(44, 36, 23, 0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </div>
              <PillSelector
                value={level}
                options={levels}
                onChange={setLevel}
                isActive={false}
                showIcon={true}
                testId="wavelength-level-selector"
              />
            </div>

            {/* Info button */}
            <button
              onClick={() => setShowTypeInfo(true)}
              className="p-2 rounded-full text-charcoal/30 hover:text-charcoal/60 hover:bg-charcoal/5 transition-colors self-center"
              data-testid="type-info-button"
            >
              <Info size={16} />
            </button>
          </motion.div>
          
          {/* Back link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8"
          >
            <button
              onClick={() => navigate('/')}
              className="text-charcoal/50 hover:text-charcoal text-sm transition-colors"
              data-testid="back-to-home"
            >
              ← Back to questions
            </button>
          </motion.div>
        </motion.div>
      </main>
      
      <InfoModal isOpen={showTypeInfo} onClose={() => setShowTypeInfo(false)} />

      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#FFF8F0',
            border: '1px solid rgba(255, 120, 10, 0.2)',
            color: '#2C2417',
          },
        }}
      />
      </div>{/* end content-layer */}
    </div>
  );
};

export default WavelengthNew;
